import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { AlertCircle } from 'lucide-react';
import { validateSignalDraft, hasErrors } from '../../../lib/canEndianness/validation';
import { computeRawRange, computePhysicalRange } from '../../../lib/canEndianness/decode';

// Shared add/edit form for a signal. Used both for the "+ Add Signal" button
// (blank draft) and for a bit-range derived from clicking the grid
// (prefilled startBit/length/byteOrder, name left for the user to fill in).
const SignalFormDialog = ({ open, mode, initialSignal, dlc, otherSignals, onSave, onClose }) => {
  const [draft, setDraft] = useState(initialSignal);
  // Physical Min/Max auto-suggest from the bit-representable range while the user
  // hasn't typed into those fields directly. Once they do, their entry is treated
  // as authoritative (matching real .dbc files, where the documented range can be
  // narrower than what the raw bits could hold) and stops being overwritten by
  // further Length/Type/Factor/Offset edits. Editing an existing signal starts
  // "touched" since it already carries a deliberately-saved range.
  const [physicalMinTouched, setPhysicalMinTouched] = useState(mode === 'edit');
  const [physicalMaxTouched, setPhysicalMaxTouched] = useState(mode === 'edit');

  useEffect(() => {
    if (open) {
      setDraft(initialSignal);
      setPhysicalMinTouched(mode === 'edit');
      setPhysicalMaxTouched(mode === 'edit');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSignal]);

  const errors = draft ? validateSignalDraft(draft, dlc, otherSignals) : {};
  const invalid = hasErrors(errors);

  const patch = (fields) => setDraft((d) => ({ ...d, ...fields }));

  const canPreview = !!draft && !errors.startBit && !errors.length && Number.isFinite(draft.factor) && Number.isFinite(draft.offset);
  const rawRange = canPreview ? computeRawRange(draft) : null;
  const naturalPhysicalRange = canPreview ? computePhysicalRange(draft) : null;

  useEffect(() => {
    if (!canPreview) return;
    if (!physicalMinTouched) patch({ physicalMin: naturalPhysicalRange.min });
    if (!physicalMaxTouched) patch({ physicalMax: naturalPhysicalRange.max });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft && draft.length, draft && draft.signed, draft && draft.factor, draft && draft.offset, canPreview]);

  if (!draft) return null;

  const handleSave = () => {
    if (invalid) return;
    onSave({ ...draft, name: draft.name.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="can-endianness-signal-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo uppercase tracking-tight">
            {mode === 'edit' ? 'Edit Signal' : 'Add Signal'}
          </DialogTitle>
          <DialogDescription>
            Define the signal's bit position, byte order, scaling, and unit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Signal Name</Label>
            <Input
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="e.g. VehicleSpeed"
              className={errors.name ? 'border-destructive' : ''}
              data-testid="can-endianness-signal-name-input"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Start Bit</Label>
              <Input
                type="number"
                min={0}
                max={63}
                value={draft.startBit}
                onChange={(e) => patch({ startBit: parseInt(e.target.value, 10) })}
                className={`font-mono ${errors.startBit ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-startbit-input"
              />
              {errors.startBit && <p className="text-xs text-destructive">{errors.startBit}</p>}
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Length (bits)</Label>
              <Input
                type="number"
                min={1}
                max={64}
                value={draft.length}
                onChange={(e) => patch({ length: parseInt(e.target.value, 10) })}
                className={`font-mono ${errors.length ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-length-input"
              />
              {errors.length && <p className="text-xs text-destructive">{errors.length}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Byte Order</Label>
              <Select value={draft.byteOrder} onValueChange={(v) => patch({ byteOrder: v })}>
                <SelectTrigger data-testid="can-endianness-signal-byteorder-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intel">Intel (Little Endian) @1</SelectItem>
                  <SelectItem value="motorola">Motorola (Big Endian) @0</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
              <Select value={draft.signed ? 'signed' : 'unsigned'} onValueChange={(v) => patch({ signed: v === 'signed' })}>
                <SelectTrigger data-testid="can-endianness-signal-signed-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unsigned">Unsigned</SelectItem>
                  <SelectItem value="signed">Signed (2's complement)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Factor</Label>
              <Input
                type="number"
                step="any"
                value={draft.factor}
                onChange={(e) => patch({ factor: parseFloat(e.target.value) })}
                className={`font-mono ${errors.factor ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-factor-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Offset</Label>
              <Input
                type="number"
                step="any"
                value={draft.offset}
                onChange={(e) => patch({ offset: parseFloat(e.target.value) })}
                className={`font-mono ${errors.offset ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-offset-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Unit</Label>
              <Input
                value={draft.unit}
                onChange={(e) => patch({ unit: e.target.value })}
                placeholder="km/h"
                data-testid="can-endianness-signal-unit-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Physical Min</Label>
              <Input
                type="number"
                step="any"
                value={draft.physicalMin}
                onChange={(e) => { setPhysicalMinTouched(true); patch({ physicalMin: parseFloat(e.target.value) }); }}
                className={`font-mono ${errors.physicalMin ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-physicalmin-input"
              />
              {errors.physicalMin && <p className="text-xs text-destructive">{errors.physicalMin}</p>}
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Physical Max</Label>
              <Input
                type="number"
                step="any"
                value={draft.physicalMax}
                onChange={(e) => { setPhysicalMaxTouched(true); patch({ physicalMax: parseFloat(e.target.value) }); }}
                className={`font-mono ${errors.physicalMax ? 'border-destructive' : ''}`}
                data-testid="can-endianness-signal-physicalmax-input"
              />
              {errors.physicalMax && <p className="text-xs text-destructive">{errors.physicalMax}</p>}
            </div>
          </div>

          {canPreview && (
            <div className="rounded-md bg-muted/40 px-3 py-2.5 space-y-1.5" data-testid="can-endianness-signal-dialog-preview">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Live Preview</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                <span className="text-muted-foreground">Raw Range (bit width)</span>
                <span>{rawRange.min.toString()} .. {rawRange.max.toString()}</span>
                <span className="text-muted-foreground">Physical Range (from bits)</span>
                <span>{naturalPhysicalRange.min} .. {naturalPhysicalRange.max} {draft.unit}</span>
              </div>
              <p className="text-[10px] text-muted-foreground pt-1 border-t border-structure/60">
                Physical = Raw × Factor + Offset &nbsp;·&nbsp; Raw = (Physical − Offset) ÷ Factor. Physical Min/Max above default to this
                bit-derived range but can be set narrower to document the signal's actual valid range.
              </p>
            </div>
          )}

          {(errors.bounds || errors.overlap) && (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="can-endianness-signal-dialog-error">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errors.bounds || errors.overlap}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="can-endianness-signal-cancel-btn">
            Cancel
          </Button>
          <Button type="button" className="btn-premium" onClick={handleSave} disabled={invalid} data-testid="can-endianness-signal-save-btn">
            {mode === 'edit' ? 'Save Changes' : 'Add Signal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignalFormDialog;
